import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetProjectVersionSyncsStatsQuery,
  GetProjectVersionSyncsStatsData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface GetProjectVersionSyncsStatsState
  extends MergeRequestState<GetProjectVersionSyncsStatsData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.getProjectVersionSyncsStats,
  isFetching: (state: ReduxState) =>
    state.server.getProjectVersionSyncsStats.isFetching,
  data: (state: ReduxState) => state.server.getProjectVersionSyncsStats.data,
  error: (state: ReduxState) => state.server.getProjectVersionSyncsStats.error,
};

const initialState: GetProjectVersionSyncsStatsState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/getProjectVersionSyncsStats';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      query,
      config,
    }: { query: GetProjectVersionSyncsStatsQuery; config?: ServerApiConfig },
    { rejectWithValue },
  ) =>
    serverApi.getProjectVersionSyncsStats(query, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerGetProjectVersionSyncsStats {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const getProjectVersionSyncsStats: ServerGetProjectVersionSyncsStats = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
